/**
 * B549 (7) — the organiser's hosted accept-&-pay page (447:2): the words and
 * the routing, pure. The page lives at `/q#<token>` on the tenant's hosted
 * storefront: the signed capability token (RULED 2026-09-25) rides the URL
 * FRAGMENT, which browsers never send to a server — so it never lands in a
 * CDN access log or a Referer header. The server is the only judge of the
 * token; this module reads its tenant only to CLAIM it on the wire (a forged
 * claim is refused there, as the uniform miss).
 */
import { dayMonthWords, clockWords } from "./when.js";
import { formatMoney } from "./money.js";

export type QuoteLinkState = "OPEN" | "ACCEPTED" | "PAID" | "EXPIRED" | "REPLACED" | "WITHDRAWN";
export type QuotePayBy = "CARD" | "BANK_TRANSFER";
export type InvoiceKind = "DEPOSIT" | "BALANCE" | "FULL";

export interface QuotePageInstalment {
  readonly kind: InvoiceKind;
  readonly number?: string;
  readonly amountMinor: number;
  readonly dueAt: number;
  readonly afterVisit: boolean;
  readonly paidMinor: number;
}

export type QuoteBalanceTerms =
  | { readonly kind: "ON_ACCEPTANCE" }
  | { readonly kind: "DAYS_BEFORE_VISIT"; readonly days: number }
  | { readonly kind: "NET_AFTER_VISIT"; readonly days: number };

export interface QuotePageQuote {
  readonly organisation: string;
  readonly contactName: string;
  readonly productNames: readonly string[];
  readonly visitAt: number;
  readonly headcount: number;
  readonly chaperones: number;
  readonly seats: number;
  readonly currency: string;
  readonly lines: readonly { readonly name: string; readonly qty: number; readonly groupUnitMinor: number; readonly publicUnitMinor?: number; readonly lineMinor: number }[];
  readonly totalMinor: number;
  readonly publicTotalMinor?: number;
  readonly balance: QuoteBalanceTerms;
  readonly schedule: readonly QuotePageInstalment[];
  readonly payFirst?: { readonly kind: InvoiceKind; readonly amountMinor: number; readonly dueAt: number; readonly dueNow: boolean } | undefined;
  readonly payOnline: boolean;
  readonly collectVia: "HOSTED_INVOICE" | "PAY_PAGE" | "BANK_TRANSFER";
  readonly bookingRef?: string;
}

/** group.quote.open's `page` (contracts/groups.ts `zQuotePage`). */
export interface QuotePageFace {
  readonly state: QuoteLinkState;
  readonly seller: string;
  readonly timeZone: string;
  readonly expiresAt: number;
  readonly quote?: QuotePageQuote | undefined;
}

/* ------------------------------------------------------------- routing */

/** `/q#<token>` (or `/q/#…`) → the token; anything else is not a quote URL. */
export function quoteTokenFrom(location: { readonly pathname: string; readonly hash: string }): string | undefined {
  if (location.pathname !== "/q" && location.pathname !== "/q/") return undefined;
  const token = location.hash.replace(/^#/, "").trim();
  return token.length > 0 ? token : undefined;
}

/** The tenant a gql1 token NAMES — read to claim it, never trusted (the server verifies). */
export function tenantOfQuoteToken(token: string): string | undefined {
  const parts = token.split(".");
  if (parts.length !== 4 || parts[0] !== "gql1") return undefined;
  try {
    const b64 = parts[1]!.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4)), (c) => c.charCodeAt(0)))) as unknown;
    const t = typeof json === "object" && json !== null ? (json as Record<string, unknown>)["tenantId"] : undefined;
    return typeof t === "string" && t.length > 0 ? t : undefined;
  } catch {
    return undefined;
  }
}

/* --------------------------------------------------------------- words */

const money = (minor: number, currency: string): string => formatMoney({ amount: minor, currency });
const yearOf = (ms: number, timeZone: string): string => new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric" }).format(ms);

export function quoteRefWords(page: QuotePageFace): string {
  const ref = page.quote?.bookingRef;
  return ref === undefined ? "GROUP QUOTE" : `GROUP BOOKING ${ref}`;
}

export function expiryWords(page: QuotePageFace): string {
  const seats = page.quote?.seats;
  const until = dayMonthWords(page.expiresAt, { locale: "en-US", timeZone: page.timeZone });
  return seats === undefined
    ? `This quote holds its seats until ${until}`
    : `This quote holds ${seats} seats until ${until} — accept before then to keep them`;
}

export function preparedForWords(q: QuotePageQuote): string {
  return `PREPARED FOR ${q.organisation} · ${q.contactName}`.toUpperCase();
}

export function ledeWords(q: QuotePageQuote, timeZone: string): string {
  const day = `${dayMonthWords(q.visitAt, { locale: "en-US", timeZone })} ${yearOf(q.visitAt, timeZone)}`;
  const party = q.chaperones > 0 ? `${q.seats} guests (${q.headcount} + ${q.chaperones} chaperone${q.chaperones === 1 ? "" : "s"})` : `${q.seats} guests`;
  return [q.productNames.join(" + "), day, `at ${clockWords(q.visitAt, { locale: "en-US", timeZone })}`, party].join(" · ");
}

export function instalmentTitle(i: QuotePageInstalment, totalMinor: number): string {
  if (i.kind === "BALANCE") return "Balance";
  if (i.kind === "FULL") return "Full amount";
  const pct = totalMinor > 0 ? (i.amountMinor * 100) / totalMinor : 0;
  return Number.isInteger(pct) && pct > 0 ? `Deposit · ${pct}%` : "Deposit";
}

export function termsWords(q: QuotePageQuote): string {
  const hasDeposit = q.schedule.some((i) => i.kind === "DEPOSIT");
  const hold = hasDeposit ? "seats are held until the deposit lands" : "seats are held until the option date";
  const what = hasDeposit ? "the balance" : "the invoice";
  const due =
    q.balance.kind === "ON_ACCEPTANCE"
      ? `${what} is due on acceptance`
      : q.balance.kind === "DAYS_BEFORE_VISIT"
        ? `${what} is due ${q.balance.days} days before the visit`
        : `${what} is due ${q.balance.days} days after the visit`;
  return `Terms: ${hold}; ${due}.`;
}

/** What pressing the one primary does, in each state — undefined = there is nothing to press. */
export function ctaWords(page: QuotePageFace, payBy: QuotePayBy): string | undefined {
  const q = page.quote;
  if (q === undefined) return undefined;
  const pays = q.payOnline && payBy === "CARD" && q.payFirst !== undefined;
  if (page.state === "OPEN") return pays ? `ACCEPT & PAY ${money(q.payFirst!.amountMinor, q.currency)}` : "ACCEPT QUOTE";
  if (page.state === "ACCEPTED" && pays) return `PAY ${money(q.payFirst!.amountMinor, q.currency)}`;
  return undefined;
}

/** A dead link: why, and who to ask. */
export function deadWords(page: QuotePageFace): { readonly title: string; readonly body: string } {
  const until = dayMonthWords(page.expiresAt, { locale: "en-US", timeZone: page.timeZone });
  if (page.state === "EXPIRED") {
    return { title: "This quote's hold has ended", body: `The seats were held until ${until}. Ask ${page.seller} for a fresh quote — they can hold seats again while there is room.` };
  }
  if (page.state === "REPLACED") {
    return { title: "There's a newer version of this quote", body: `${page.seller} changed this quote after sending it. Use the link in their latest email — this one no longer opens it.` };
  }
  return { title: "This quote was withdrawn", body: `${page.seller} withdrew this quote and released its seats. Get in touch with them if your plans still stand.` };
}

/** What happens after the press, where the card is not taken on this page. */
export function afterAcceptWords(page: QuotePageFace, payBy: QuotePayBy, inline = false): string {
  const q = page.quote;
  const first = q?.payFirst;
  const inv = first === undefined || q === undefined ? undefined : q.schedule.find((i) => i.kind === first.kind)?.number;
  const what = first === undefined ? "the invoice" : first.kind === "DEPOSIT" ? "the deposit invoice" : "the invoice";
  if (q !== undefined && !q.payOnline) return `${page.seller} sends ${what} by email — pay it the way it says.`;
  if (payBy === "BANK_TRANSFER") return `${page.seller} sends the transfer details for ${inv ?? what} by email. Your seats stay held until the money lands.`;
  if (inline) return "Your card is taken securely by the payment provider — it never touches this page's server.";
  return `You pay on ${page.seller}'s secure payment page — card, Apple Pay or Google Pay.`;
}

/* ---------------------------------------------------------- controller */

/** B549 (7c): the invoice's own payment, confirmed inline by the provider's element. */
export interface QuoteInlinePay {
  readonly provider: "stripe";
  readonly clientSecret: string;
  readonly publishableKey: string;
  readonly stripeAccount?: string;
  readonly amountMinor: number;
  readonly currency: string;
}

/** What group.quote.respond's answer asks the page to do next: pay inline, go to the provider's page, or redraw. */
export function respondOutcome(body: { readonly page?: QuotePageFace; readonly payUrl?: string; readonly pay?: QuoteInlinePay }):
  | { readonly kind: "inline"; readonly pay: QuoteInlinePay; readonly page: QuotePageFace | undefined; readonly fallbackUrl?: string }
  | { readonly kind: "pay"; readonly url: string }
  | { readonly kind: "page"; readonly page: QuotePageFace | undefined } {
  const safeUrl = body.payUrl !== undefined && /^https:\/\//.test(body.payUrl) ? body.payUrl : undefined;
  if (body.pay !== undefined) return { kind: "inline", pay: body.pay, page: body.page, ...(safeUrl !== undefined ? { fallbackUrl: safeUrl } : {}) };
  if (safeUrl !== undefined) return { kind: "pay", url: safeUrl };
  return { kind: "page", page: body.page };
}

/**
 * A refusal in the ORGANISER's voice. The server's 409 sentences are the
 * desk's ("send a new quote…"); the organiser is told what it means for them
 * and who to ask. Wire failures keep the client's own retry sentence.
 */
export function refusalWords(err: { readonly status?: number; readonly message: string }, seller: string | undefined): string {
  const who = seller ?? "the business that sent it";
  if (err.status === 409) return `This quote can't be accepted right now — its seats may have gone back on sale. Ask ${who} for a fresh quote.`;
  if (err.status === 404 || err.status === 403) return `This link doesn't open a quote any more. Ask ${who} for a fresh link.`;
  return err.message;
}
